import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { CompanyGroupInvitationAcceptanceContext } from './company-group.model';
import { CompanyService } from './company.service';
import { AuthService } from '../security/auth.service';
import { SessionStorageService } from '../services/session-storage.service';

@Component({
    selector: 'app-company-group-invitation-acceptance',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, CardModule, InputTextModule, MessageModule],
    template: `
        <main class="invitation-page">
            <p-card header="Secure company-group invitation" styleClass="invitation-card">
                <ng-container *ngIf="loading">
                    <div class="loading"><i class="pi pi-spin pi-spinner"></i> Verifying secure invitation…</div>
                </ng-container>

                <ng-container *ngIf="errorMessage && !loading">
                    <p-message severity="error" [text]="errorMessage"></p-message>
                    <div *ngIf="approvalAccessRequired" class="approval-access">
                        <p>Company-group approval requires an explicit, company-scoped role. If you are already permitted to manage users in this tenant, you can explicitly enable that role for yourself.</p>
                        <p-button
                            label="Enable my approval access"
                            icon="pi pi-shield"
                            [loading]="acting"
                            [disabled]="acting"
                            (click)="enableApprovalAccess()"></p-button>
                    </div>
                    <div class="actions"><p-button label="Return to dashboard" severity="secondary" (click)="goToDashboard()"></p-button></div>
                </ng-container>

                <ng-container *ngIf="context && !loading && !completed">
                    <p class="eyebrow">DUAL-CONSENT CONNECTION</p>
                    <h2>{{ context.sourceCompanyName }} invites {{ context.targetCompanyName }}</h2>
                    <p>Join the <strong>{{ context.groupName }}</strong> company group.</p>
                    <p-message severity="info" [text]="context.safetyNotice"></p-message>
                    <p class="expiry">This single-use invitation expires {{ context.expiresAt | date:'medium' }}.</p>

                    <section class="reauth">
                        <h3>Confirm your identity</h3>
                        <p>For protection against forwarded links and unattended sessions, sign in again before accepting.</p>
                        <label for="currentPassword">Current password</label>
                        <div class="password-row">
                            <input id="currentPassword" pInputText type="password" [(ngModel)]="password" autocomplete="current-password" />
                            <p-button label="Verify password" [loading]="acting" [disabled]="!password || acting" (click)="acceptWithPassword()"></p-button>
                        </div>
                        <div class="provider-actions">
                            <p-button label="Verify with Google" severity="secondary" [outlined]="true" [disabled]="acting" (click)="acceptWithGoogle()"></p-button>
                            <p-button label="Verify with Microsoft" severity="secondary" [outlined]="true" [disabled]="acting" (click)="acceptWithMicrosoft()"></p-button>
                        </div>
                    </section>

                    <div class="actions">
                        <p-button label="Decline invitation" severity="danger" [text]="true" [disabled]="acting" (click)="decline()"></p-button>
                    </div>
                </ng-container>

                <ng-container *ngIf="completed">
                    <p-message severity="success" text="The company-group relationship is active. No users, roles, or company data were shared automatically."></p-message>
                    <div class="actions"><p-button label="Open Company Groups" (click)="openGroups()"></p-button></div>
                </ng-container>
            </p-card>
        </main>
    `,
    styles: [`
        .invitation-page { min-height: 100%; padding: 4rem 1rem; display: grid; place-items: start center; background: var(--surface-ground); }
        :host ::ng-deep .invitation-card { width: min(100%, 680px); }
        .eyebrow { color: var(--primary-color); font-size: .78rem; font-weight: 700; letter-spacing: .08em; margin: 0 0 .5rem; }
        h2 { margin: 0 0 .7rem; } .expiry { color: var(--text-color-secondary); font-size: .9rem; }
        .reauth { border-top: 1px solid var(--surface-border); margin-top: 1.4rem; padding-top: 1.2rem; }
        .reauth h3 { margin: 0 0 .4rem; } .reauth p { color: var(--text-color-secondary); }
        .password-row { display: flex; gap: .7rem; align-items: end; margin-top: .35rem; } .password-row input { flex: 1; }
        .provider-actions, .actions { display: flex; gap: .65rem; flex-wrap: wrap; margin-top: 1rem; }
        .approval-access { display: grid; gap: .75rem; margin-top: 1rem; padding: 1rem; border: 1px solid var(--surface-border); border-radius: .5rem; background: var(--surface-ground); }
        .approval-access p { margin: 0; color: var(--text-color-secondary); line-height: 1.45; }
        .loading { display: flex; gap: .6rem; align-items: center; color: var(--text-color-secondary); }
        @media (max-width: 600px) { .password-row { align-items: stretch; flex-direction: column; } }
    `]
})
export class CompanyGroupInvitationAcceptanceComponent implements OnInit {
    private static readonly tokenStorageKey = 'company-group-membership-invitation-token';

    context?: CompanyGroupInvitationAcceptanceContext;
    password = '';
    loading = true;
    acting = false;
    completed = false;
    errorMessage = '';
    approvalAccessRequired = false;
    private token = '';

    constructor(
        private readonly companyService: CompanyService,
        private readonly authService: AuthService,
        private readonly sessionStorageService: SessionStorageService,
        private readonly router: Router
    ) {}

    ngOnInit(): void {
        const fragmentToken = window.location.hash.startsWith('#') ? window.location.hash.substring(1) : '';
        if (fragmentToken) {
            sessionStorage.setItem(CompanyGroupInvitationAcceptanceComponent.tokenStorageKey, fragmentToken);
            window.history.replaceState({}, document.title, `${window.location.pathname}${window.location.search}`);
        }
        this.token = sessionStorage.getItem(CompanyGroupInvitationAcceptanceComponent.tokenStorageKey) || '';
        if (!this.token) {
            this.loading = false;
            this.errorMessage = 'This company-group invitation is invalid or no longer available.';
            return;
        }
        this.authService.getCurrentUserAccessToken().subscribe({
            next: token => {
                this.sessionStorageService.saveAccessToken(token, Date.now());
                this.loadContext();
            },
            error: () => this.redirectToLogin()
        });
    }

    acceptWithPassword(): void {
        this.reauthenticateAndAccept(this.authService.reauthenticateWithPassword(this.password));
    }

    acceptWithGoogle(): void {
        this.reauthenticateAndAccept(this.authService.reauthenticateWithGoogle());
    }

    acceptWithMicrosoft(): void {
        this.reauthenticateAndAccept(this.authService.reauthenticateWithMicrosoft());
    }

    decline(): void {
        if (!window.confirm('Decline this invitation? The link will stop working.')) {
            return;
        }
        this.acting = true;
        this.companyService.declineCompanyGroupMembershipInvitation(this.token).subscribe({
            next: () => {
                this.acting = false;
                this.clearToken();
                this.errorMessage = 'You declined this company-group invitation.';
            },
            error: error => this.handleActionError(error)
        });
    }

    enableApprovalAccess(): void {
        this.acting = true;
        this.companyService.enableCompanyGroupInvitationApprovalAccess(this.token).subscribe({
            next: context => {
                this.context = context;
                this.errorMessage = '';
                this.approvalAccessRequired = false;
                this.acting = false;
            },
            error: error => this.handleActionError(error)
        });
    }

    openGroups(): void { this.router.navigate(['/company/groups']); }
    goToDashboard(): void { this.router.navigate(['/dashboard']); }

    private loadContext(): void {
        this.companyService.getCompanyGroupInvitationAcceptanceContext(this.token).subscribe({
            next: context => {
                this.context = context;
                this.loading = false;
            },
            error: error => {
                this.loading = false;
                this.approvalAccessRequired = error?.status === 403;
                this.errorMessage = this.approvalAccessRequired
                    ? 'Explicit company-group approval access is required before this invitation can be reviewed.'
                    : error?.error?.message || 'This invitation is invalid, expired, or is intended for another company.';
            }
        });
    }

    private reauthenticateAndAccept(operation: ReturnType<AuthService['reauthenticateWithPassword']>): void {
        this.acting = true;
        operation.subscribe({
            next: freshToken => {
                this.sessionStorageService.saveAccessToken(freshToken, Date.now());
                this.companyService.acceptCompanyGroupMembershipInvitation(this.token).subscribe({
                    next: () => {
                        this.acting = false;
                        this.completed = true;
                        this.clearToken();
                    },
                    error: error => this.handleActionError(error)
                });
            },
            error: error => this.handleActionError(error)
        });
    }

    private handleActionError(error: any): void {
        this.acting = false;
        this.errorMessage = error?.error?.message || error?.message || 'We could not verify your identity or process this invitation.';
    }

    private redirectToLogin(): void {
        this.router.navigate(['/login'], { queryParams: { returnUrl: '/company/group-invitation/accept' } });
    }

    private clearToken(): void {
        this.token = '';
        sessionStorage.removeItem(CompanyGroupInvitationAcceptanceComponent.tokenStorageKey);
    }
}
